import Wordmark from "../components/Wordmark";

const LAST_UPDATED = "7 April 2026";
const COMPANY = "Bethany Labs";
const CONTACT_EMAIL = "hello@bethanylabs.com";
const PRODUCT = "YouSift";
const WEBSITE = "www.yousift.io";

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
      <div className="space-y-4 text-gray-400 leading-relaxed">{children}</div>
    </div>
  );
}

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-[#0F0F0F] flex flex-col">
      <header className="px-6 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <a href="/"><Wordmark size="sm" darkBg={true} /></a>
        <a href="/" className="text-gray-400 hover:text-white text-sm transition-colors">← Back</a>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500">Legal</span>
          <h1 className="text-4xl font-black text-white mt-3 mb-2">Terms of Use</h1>
          <p className="text-gray-500 text-sm">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-5 py-4 mb-10 text-gray-400 text-sm leading-relaxed">
          By using {PRODUCT} at <span className="text-white">{WEBSITE}</span>, you agree to these Terms of Use. If you do not agree, do not use the service.
        </div>

        <Section title="1. The Service">
          <p>
            {PRODUCT} is a content repurposing tool operated by {COMPANY}. It allows users to submit YouTube video URLs and receive AI-generated LinkedIn posts and blog articles based on the video's transcript.
          </p>
          <p>
            We reserve the right to modify, suspend, or discontinue any aspect of the service at any time with reasonable notice where possible.
          </p>
        </Section>

        <Section title="2. Eligibility">
          <p>
            You must be at least 18 years old to use {PRODUCT}. By using the service, you confirm that you meet this requirement and that you are using it for lawful purposes.
          </p>
        </Section>

        <Section title="3. Acceptable Use">
          <p>You agree not to use {PRODUCT} to:</p>
          <ul className="space-y-2 mt-2">
            {[
              "Submit URLs for videos you do not have the right to repurpose.",
              "Generate content that is defamatory, misleading, harassing, or unlawful.",
              "Circumvent usage limits through technical means or by creating multiple accounts.",
              "Attempt to reverse-engineer, scrape, or otherwise extract data from the service.",
              "Use the service in a way that places excessive or unreasonable load on our infrastructure.",
              "Resell access to the service without prior written consent from Bethany Labs.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] mt-2 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="4. Content and Intellectual Property">
          <p>
            You retain full ownership of any content generated by {PRODUCT} from your submitted videos. {COMPANY} does not claim any rights over the outputs.
          </p>
          <p>
            You are solely responsible for ensuring you have the right to repurpose content from any YouTube video you submit. {PRODUCT} is a tool — it does not verify copyright ownership on your behalf.
          </p>
          <p>
            The {PRODUCT} name, logo, branding, and underlying software remain the intellectual property of {COMPANY}. Nothing in these Terms grants you any rights to our trademarks or proprietary technology.
          </p>
        </Section>

        <Section title="5. Plans, Payments, and Refunds">
          <p>
            {PRODUCT} offers monthly and lifetime access plans. All prices are displayed inclusive of any applicable taxes and are charged in GBP.
          </p>
          <p>
            Payments are processed by Stripe on behalf of {COMPANY}. By purchasing a plan, you authorise Stripe to charge your payment method in accordance with the plan you selected.
          </p>
          <p>
            Monthly subscriptions renew automatically at the end of each billing period. You may cancel at any time through your Stripe billing portal; cancellation takes effect at the end of the current billing period.
          </p>
          <p>
            We offer a <span className="text-white font-medium">7-day money-back guarantee</span> on all plans. If you are not satisfied within 7 days of purchase, contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF0000] hover:underline">{CONTACT_EMAIL}</a>{" "}
            for a full refund, no questions asked.
          </p>
          <p>
            After 7 days, all sales are final. Lifetime access purchases are non-refundable outside of this window.
          </p>
        </Section>

        <Section title="6. Availability and Uptime">
          <p>
            We aim to keep {PRODUCT} available at all times but do not guarantee uninterrupted access. The service may be unavailable due to maintenance, third-party outages, or circumstances beyond our control.
          </p>
          <p>
            We are not liable for any losses resulting from service downtime or interruption.
          </p>
        </Section>

        <Section title="7. AI-Generated Content">
          <p>
            Content generated by {PRODUCT} is produced by an AI model and should be reviewed before publication. While we instruct the AI to produce accurate and professional output, we cannot guarantee that all generated content will be factually correct, complete, or suitable for your specific purpose.
          </p>
          <p>
            You are solely responsible for reviewing, editing, and taking ownership of any content you publish that was generated using {PRODUCT}. {COMPANY} accepts no liability for any consequences arising from the publication of AI-generated content.
          </p>
        </Section>

        <Section title="8. Limitation of Liability">
          <p>
            To the maximum extent permitted by law, {COMPANY} shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of {PRODUCT}, including but not limited to loss of profits, data, or business opportunities.
          </p>
          <p>
            Our total liability to you for any claim arising out of or relating to these Terms or your use of the service shall not exceed the amount you paid to {COMPANY} in the 12 months preceding the claim.
          </p>
        </Section>

        <Section title="9. Indemnification">
          <p>
            You agree to indemnify and hold harmless {COMPANY} and its directors, employees, and contractors from any claims, damages, or expenses (including legal fees) arising from your use of the service, your breach of these Terms, or your infringement of any third-party rights.
          </p>
        </Section>

        <Section title="10. Governing Law">
          <p>
            These Terms are governed by the laws of England and Wales. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>
        </Section>

        <Section title="11. Changes to These Terms">
          <p>
            We may update these Terms from time to time. We will notify users of material changes by updating the "Last updated" date. Continued use of {PRODUCT} after changes are posted constitutes your acceptance of the revised Terms.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            For any questions regarding these Terms, contact {COMPANY} at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF0000] hover:underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>
      </main>

      <footer className="py-8 text-center border-t border-[#1A1A1A]">
        <p className="text-gray-700 text-xs">
          &copy; {new Date().getFullYear()}{" "}
          <span className="text-gray-500 font-semibold">{COMPANY}</span> · All rights reserved
        </p>
      </footer>
    </div>
  );
}
